import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserPlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function Staff() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    try { const r = await api.get("/users"); setUsers(r.data); }
    catch { toast.error("Failed"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-8" data-testid="staff-page">
      <div className="flex items-end justify-between border-b border-slate-200 pb-6">
        <div>
          <div className="overline text-slate-500">Access control</div>
          <h1 className="font-display text-4xl font-black">Staff</h1>
          <p className="text-slate-500 mt-2">Owners have full control. Staff can record collections, expenses & movements.</p>
        </div>
        <AddDialog onDone={load} />
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 border-b border-slate-200">
              <TableHead className="uppercase text-xs tracking-wider">Name</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Email</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Role</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                <TableCell className="font-semibold">{u.name}</TableCell>
                <TableCell className="text-slate-600 font-mono-num">{u.email}</TableCell>
                <TableCell>
                  <span className="overline" style={{ color: u.role === "owner" ? "#002FA7" : "#475569" }}>{u.role}</span>
                </TableCell>
                <TableCell>
                  {u.id !== user.id && (
                    <button
                      onClick={async () => {
                        if (!confirm(`Delete user ${u.email}?`)) return;
                        try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
                        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                      }}
                      className="text-slate-400 hover:text-[#FF2400]"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <TrashIcon size={16} />
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function AddDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [role, setRole] = useState("staff");
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { name, email, password: pw, role, branch_id: branchId || null });
      toast.success("User added");
      setOpen(false); setName(""); setEmail(""); setPw(""); setRole("staff"); setBranchId("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white" data-testid="add-user-button">
          <UserPlusIcon size={16} className="mr-2" /> Add Person
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300">
        <DialogHeader><DialogTitle className="font-display">New user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-user-form">
          <div>
            <Label className="overline">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required className="rounded-none" data-testid="user-name-input" />
          </div>
          <div>
            <Label className="overline">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-none" data-testid="user-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Password</Label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} className="rounded-none" data-testid="user-password-input" />
            </div>
            <div>
              <Label className="overline">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="rounded-none" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="delivery_entry">Delivery Entry</SelectItem>
                  <SelectItem value="accountant">Accountant</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="owner">Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="overline">Branch (optional — leave blank for HQ / all-branch access)</Label>
            <Select value={branchId || "__none"} onValueChange={(v) => setBranchId(v === "__none" ? "" : v)}>
              <SelectTrigger className="rounded-none" data-testid="user-branch-select"><SelectValue placeholder="No branch (HQ)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">No branch (HQ / all)</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-brand-blue hover:bg-brand-blue-ink" data-testid="user-submit-button">Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
